<template>
    <footer class="footer" id="footer">
        <div class="container">
            <div class="footer__content">
                <div class="footer__content__contacts">
                    <div class="footer__content__contacts__socials" v-if="globalSettings?.social_media?.length">
                        <h5>
                            {{
                                footerSettings?.buttons?.socialBlockName
                                    ? footerSettings?.buttons?.socialBlockName
                                    : $t('followUs')
                            }}
                        </h5>
                        <div class="footer__content__contacts__socials__icons">
                            <div
                                class="footer__content__contacts__socials__icons__icon"
                                v-for="item in globalSettings.social_media"
                                :key="'soc' + item?.id"
                            >
                                <a v-if="item?.icon || generateDefImg(item?.key?.slug)" :href="item?.link">
                                    <img :src="item?.icon || generateDefImg(item?.key?.slug)" />
                                </a>

                                <MyLink
                                    tag="a"
                                    v-if="item?.key?.slug"
                                    :to="item?.link"
                                    :title="item?.key?.slug[0].toUpperCase() + item?.key?.slug?.slice(1)"
                                    rel="nofollow"
                                />
                            </div>
                        </div>
                        <template v-if="globalSettings?.main_email">
                            <h5>
                                {{ $t('becomePartner.emailPlaceholder') }}
                            </h5>
                            <span :style="{ color: 'var(--btn-text-white)', fontSize: '20px' }">{{
                                globalSettings?.main_email
                            }}</span>
                        </template>
                    </div>

                    <div class="footer__content__contacts__phone">
                        <h5>
                            {{
                                footerSettings?.buttons?.contactBlockName
                                    ? footerSettings?.buttons?.contactBlockName
                                    : $t('contactUs')
                            }}
                        </h5>
                        <a :href="`tel:${mainPhoneNumber}`">{{ mainPhoneNumber }}</a>
                        <a v-if="stationaryPhoneNumber" :href="`tel:${stationaryPhoneNumber}`">{{ stationaryPhoneNumber }}</a>
                    </div>
                </div>
                <div class="footer__mobile" v-if="isUnderOrEqualViewport768">
                    <template v-for="(column, index) in columns">
                        <MyAccordion сlass="footer__mobile__accordions__item" type="linkMenu" :title="column.title">
                            <Column :column="column" :key="index" />
                        </MyAccordion>
                    </template>

                    <MyAccordion
                        сlass="footer__mobile__accordions__item"
                        type="linkMenu"
                        :title="
                            footerSettings?.buttons?.deliveryBlockName
                                ? footerSettings?.buttons?.deliveryBlockName
                                : $t('howToFindUs')
                        "
                    >
                        <div class="footer__content--row__contacts">
                            <h5>
                                {{
                                    footerSettings?.buttons?.deliveryBlockName
                                        ? footerSettings?.buttons?.deliveryBlockName
                                        : $t('howToFindUs')
                                }}
                            </h5>

                            <div class="footer__content--row__contacts__orders">
                                <p>{{ footerSettings?.issuanceOfOnlineOrders }}</p>
                            </div>
                            <hr />
                            <div class="footer__content--row__contacts__available">
                                <p v-if="footerSettings?.workingTime?.workingTimeWeekday">
                                    {{ footerSettings?.workingTime?.workingTimeWeekday }}
                                </p>
                                <p v-if="footerSettings?.workingTime?.workingTimeWeekends">
                                    {{ footerSettings?.workingTime?.workingTimeWeekends }}
                                </p>
                            </div>
                        </div>
                    </MyAccordion>
                </div>
                <div class="footer__content--row" v-else-if="isContactsBlockHasContent || columns?.length">
                    <template v-if="columns?.length">
                        <Column v-for="(column, index) in columns" :column="column" :key="index" />
                    </template>

                    <div v-if="isContactsBlockHasContent" class="footer__content--row__contacts">
                        <h5>
                            {{
                                footerSettings?.buttons?.deliveryBlockName
                                    ? footerSettings?.buttons?.deliveryBlockName
                                    : $t('howToFindUs')
                            }}
                        </h5>

                        <div class="footer__content--row__contacts__orders">
                            <p>{{ footerSettings?.issuanceOfOnlineOrders }}</p>
                        </div>
                        <hr />
                        <div class="footer__content--row__contacts__available">
                            <p v-if="footerSettings?.workingTime?.workingTimeWeekday">
                                {{ footerSettings?.workingTime?.workingTimeWeekday }}
                            </p>
                            <p v-if="footerSettings?.workingTime?.workingTimeWeekends">
                                {{ footerSettings?.workingTime?.workingTimeWeekends }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="developer?.company_year || developer?.active_developer" class="footer__content--copyright-block">
                    <div v-if="!developer?.hide_year" class="footer__content--copyright-block-copyright">
                        © {{ developer?.company_year || '2013' }} – {{ new Date().getFullYear() }}
                        {{ developer?.company_name || 'ECOM' }}
                    </div>
                    <div class="footer__content--copyright-block-developer" v-if="getDevelopersInfo.length > 0">
                        <a :href="footerSettings.developers[0]?.link" target="_blank">
                            <img
                                v-if="footerSettings.developers[0]?.image"
                                :src="footerSettings.developers[0]?.image"
                                :alt="footerSettings.developers[0]?.text"
                            />
                            <template v-else>
                                {{ footerSettings.developers[0]?.text }}
                            </template>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>

<script>
import { mapGetters } from 'vuex';
import viewport from '~/mixins/viewport';
import generateDefImageIcon from '~/mixins/generate-def-image-icon';

export default {
    name: 'Footer',
    mixins: [viewport, generateDefImageIcon],
    components: {
        Column: () => import('./components/Column'),
        MyLink: () => import('@/themes/ecaterix/components/ui/MyLinkBtn'),
        MyAccordion: () => import('@/themes/ecaterix/components/ui/MyAccordion'),
    },
    watch: {
        '$i18n.locale': {
            handler(e) {
                this.$store.dispatch('footer/fetchFooterSettings');
            },
        },
    },
    computed: {
        ...mapGetters({
            globalSettings: 'global/globalSettings',
            footerSettings: 'footer/footerSettings',
            mainPhoneNumber: 'global/mainPhoneNumber',
            stationaryPhoneNumber: 'global/stationaryPhoneNumber',
            contacts: 'the-contacts/getShopsLocations',
            categories: 'categories/getCategories',
            developer: 'footer/getDeveloper',
        }),
        columns() {
            return [...this.generateFooterColumns];
        },
        generateFooterColumns() {
            return (
                this.footerSettings?.columns?.map(column => {
                    return {
                        title: column.name,
                        items: column.items.map(item => {
                            return {
                                id: item.id,
                                type: item.link && item.link.includes('http') ? 'tagLink' : 'link',
                                label: item.name,
                                link: item.link,
                            };
                        }),
                    };
                }) || []
            );
        },
        getDevelopersInfo() {
            return this.footerSettings?.developers || [];
        },
        catalogColumn() {
            const catalogItems = this.categories.map(item => {
                return {
                    id: item.id,
                    type: 'link',
                    label: item.categoryTitle,
                    link: this.localePath({ path: `${item.categoryUrl}` }),
                };
            });

            return { title: this.$t('catalog'), items: catalogItems };
        },
        informationColumn() {
            const links = [
                { id: 1, type: 'link', label: this.$t('aboutUs'), link: this.localePath('/about') },
                { id: 2, type: 'link', label: this.$t('blog'), link: this.localePath('/articles') },
                { id: 3, type: 'link', label: this.$t('partners'), link: this.localePath('/partners') },
                { id: 4, type: 'link', label: this.$t('reviews'), link: this.localePath('/feedbacks') },
                { id: 5, type: 'link', label: this.$t('gallery'), link: this.localePath('/gallery') },
                { id: 6, type: 'link', label: this.$t('logistics'), link: this.localePath('/logistics') },
                { id: 7, type: 'link', label: this.$t('faq'), link: '/#faq', scroll: true },
                { id: 8, type: 'link', label: this.$t('contacts'), link: this.localePath('/contacts') },
            ];

            return {
                title: this.$t('information'),
                items: links.sort((a, b) => b.id + a.id),
            };
        },
        isContactsBlockHasContent() {
            return (
                this.footerSettings?.workingTime?.workingTimeWeekday ||
                this.footerSettings?.issuanceOfOnlineOrders ||
                this.footerSettings?.workingTime?.workingTimeWeekends
            );
        },
    },
    async fetch() {
        // if (this.footerSettings === null) {
        await this.$store.dispatch('footer/fetchFooterSettings');
        // }

        // if (this.footerSettings === null || !!this.contacts?.length) {
        await this.$store.dispatch('the-contact/fetchShopsLocations');
        // }
    },
};
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
